// Thin wrapper around YouTube's IFrame Player API. The whole point of this
// composable is to give us *programmatic* control over an embedded YouTube
// video — onReady / onPlay / onPause / onEnded events, plus get-current-time
// polling so we can drive UI (gate buttons, save resume points, etc).
//
// API ref: https://developers.google.com/youtube/iframe_api_reference
//
// Caller is expected to mount its own <div> first, then call this composable
// with the element ref. The composable handles loading the IFrame API once
// globally and tearing the player down on unmount.

import {
  translatedCaptionTrack,
  youtubeCaptionLang,
  youtubeCaptionParams,
  type YoutubeCaptionTrack,
} from '~/utils/youtubeCaptions';

declare global {
  interface Window {
    YT?: {
      Player: new (
        el: HTMLElement,
        opts: {
          videoId?: string;
          playerVars?: Record<string, string | number>;
          events?: {
            onReady?: (event: { target: YTPlayerInstance }) => void;
            onStateChange?: (event: { data: number; target: YTPlayerInstance }) => void;
            onError?: (event: { data: number }) => void;
          };
        },
      ) => YTPlayerInstance;
      PlayerState: { UNSTARTED: -1; ENDED: 0; PLAYING: 1; PAUSED: 2; BUFFERING: 3; CUED: 5 };
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

export type YTPlayerInstance = {
  playVideo: () => void;
  pauseVideo: () => void;
  seekTo: (seconds: number, allowSeekAhead?: boolean) => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  destroy: () => void;
  getPlayerState?: () => number;
  getOption?: (module: string, option: string) => unknown;
  setOption?: (module: string, option: string, value: unknown) => void;
};

type YTReadyResolver = () => void;
let apiReadyPromise: Promise<void> | null = null;
const apiReadyResolvers: YTReadyResolver[] = [];

export const loadYouTubeApi = (): Promise<void> => {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.YT?.Player) return Promise.resolve();
  if (apiReadyPromise) return apiReadyPromise;

  apiReadyPromise = new Promise<void>((resolve) => {
    apiReadyResolvers.push(resolve);

    // YouTube calls window.onYouTubeIframeAPIReady once the API is ready.
    // Several composables might race for this — fire all queued resolvers.
    const existing = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      existing?.();
      for (const r of apiReadyResolvers.splice(0)) r();
    };

    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    tag.async = true;
    document.head.appendChild(tag);
  });

  return apiReadyPromise;
};

// Captions load a moment after playback starts, so retry until there is a track to translate.
export const applyCaptionTranslation = (p: YTPlayerInstance, lang: string): (() => void) => {
  let tries = 0;
  const timer = setInterval(() => {
    tries += 1;
    try {
      const current = p.getOption?.('captions', 'track') as YoutubeCaptionTrack | undefined;
      const next = translatedCaptionTrack(current, lang);
      if (next) p.setOption?.('captions', 'track', next);
      if (current?.languageCode || tries >= 15) clearInterval(timer);
    } catch {
      clearInterval(timer);
    }
  }, 1000);
  return () => clearInterval(timer);
};

export type UseYoutubePlayerOptions = {
  videoId: MaybeRefOrGetter<string>;
  el: Ref<HTMLElement | null>;
  startSeconds?: MaybeRefOrGetter<number | undefined>;
  autoplay?: boolean;
  onReady?: (player: YTPlayerInstance) => void;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  onProgress?: (info: { current: number; duration: number; percent: number }) => void;
};

export const useYoutubePlayer = (opts: UseYoutubePlayerOptions) => {
  const player = shallowRef<YTPlayerInstance | null>(null);
  const { locale } = useI18n();
  let stopCaptions: (() => void) | null = null;
  const isReady = ref(false);
  const isPlaying = ref(false);
  const isEnded = ref(false);
  const currentTime = ref(0);
  const duration = ref(0);
  const percent = computed(() => (duration.value > 0 ? Math.min(100, (currentTime.value / duration.value) * 100) : 0));

  let progressInterval: ReturnType<typeof setInterval> | null = null;

  const stopProgressPolling = () => {
    if (progressInterval) {
      clearInterval(progressInterval);
      progressInterval = null;
    }
  };

  const startProgressPolling = () => {
    stopProgressPolling();
    progressInterval = setInterval(() => {
      const p = player.value;
      if (!p) return;
      try {
        const cur = p.getCurrentTime();
        const dur = p.getDuration();
        currentTime.value = cur;
        duration.value = dur;
        if (dur > 0) {
          opts.onProgress?.({ current: cur, duration: dur, percent: Math.min(100, (cur / dur) * 100) });
        }
      } catch {
        // The player can throw before it's truly ready; ignore.
      }
    }, 500);
  };

  const create = async () => {
    if (typeof window === 'undefined') return;
    const target = opts.el.value;
    if (!target) return;

    const videoId = toValue(opts.videoId);
    if (!videoId) return;

    await loadYouTubeApi();
    if (!window.YT?.Player) return;

    isReady.value = false;
    isEnded.value = false;
    isPlaying.value = false;

    player.value = new window.YT.Player(target, {
      videoId,
      playerVars: {
        autoplay: opts.autoplay ? 1 : 0,
        rel: 0,
        modestbranding: 1,
        playsinline: 1,
        start: toValue(opts.startSeconds) ?? 0,
        ...youtubeCaptionParams(locale.value),
      },
      events: {
        onReady: ({ target: p }) => {
          isReady.value = true;
          try {
            duration.value = p.getDuration();
          } catch {
            // Duration sometimes returns 0 before metadata loads.
          }
          opts.onReady?.(p);
        },
        onStateChange: ({ data }) => {
          const state = window.YT?.PlayerState;
          if (!state) return;
          if (data === state.PLAYING) {
            isPlaying.value = true;
            const lang = youtubeCaptionLang(locale.value);
            if (lang && player.value && !stopCaptions) stopCaptions = applyCaptionTranslation(player.value, lang);
            startProgressPolling();
            opts.onPlay?.();
          } else if (data === state.PAUSED) {
            isPlaying.value = false;
            stopProgressPolling();
            opts.onPause?.();
          } else if (data === state.ENDED) {
            isPlaying.value = false;
            isEnded.value = true;
            stopProgressPolling();
            // Snap to 100% before firing — avoids the next frame showing 99%.
            if (duration.value > 0) {
              currentTime.value = duration.value;
              opts.onProgress?.({ current: duration.value, duration: duration.value, percent: 100 });
            }
            opts.onEnded?.();
          }
        },
      },
    });
  };

  const destroy = () => {
    stopProgressPolling();
    stopCaptions?.();
    stopCaptions = null;
    try {
      player.value?.destroy();
    } catch {
      // Already destroyed or never created.
    }
    player.value = null;
    isReady.value = false;
  };

  const play = () => player.value?.playVideo();
  const pause = () => player.value?.pauseVideo();
  const seekTo = (seconds: number) => player.value?.seekTo(seconds, true);

  // Recreate when videoId changes (e.g. switching curriculum steps in a lightbox).
  watch(
    () => toValue(opts.videoId),
    () => {
      destroy();
      create();
    },
  );

  onMounted(create);
  onBeforeUnmount(destroy);

  return {
    player,
    isReady,
    isPlaying,
    isEnded,
    currentTime,
    duration,
    percent,
    play,
    pause,
    seekTo,
  };
};
